import { FormEvent, useState } from 'react';
import { api, PaymentAction, PaymentMethod, Payment } from '../api';
import { useAuth } from '../auth/AuthContext';
import { Alert, useAction } from './ui';

const methods: Array<{ id: PaymentMethod; name: string; note: string }> = [
    { id: 'swish', name: 'Swish', note: 'Pay instantly with the Swish app' },
    { id: 'stripe', name: 'Card', note: 'Visa, Mastercard via Stripe' },
    { id: 'paypal', name: 'PayPal', note: 'Pay with your PayPal account' },
    { id: 'bitcoin', name: 'Bitcoin', note: 'Pay with BTC' },
];

const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export default function PaymentForm({ orderId, onStarted }: { orderId: string; onStarted: (payment: Payment, action: PaymentAction) => void }) {
    const { user } = useAuth();
    const [method, setMethod] = useState<PaymentMethod>('swish');
    // On a phone the Swish app can be opened directly; on desktop the request is sent to a phone number.
    const [swishMode, setSwishMode] = useState<'phone' | 'app'>(isMobile() ? 'app' : 'phone');
    const [phone, setPhone] = useState(user?.phone ?? '');
    const { busy, error, run } = useAction();

    const submit = (e: FormEvent) => {
        e.preventDefault();
        void run(async () => {
            const body = method === 'swish' && swishMode === 'phone' ? { method, phone } : { method };
            const res = await api.post<{ payment: Payment; action: PaymentAction }>(`/orders/${orderId}/pay`, body);
            const { action } = res.data;
            if (action.type === 'swish_app') window.location.href = action.appUrl;
            if (action.type === 'redirect') window.location.href = action.url;
            onStarted(res.data.payment, action);
        });
    };

    return (
        <form className="fi-form" onSubmit={submit}>
            {error && <Alert type="error">{error}</Alert>}
            <div className="method-grid" role="radiogroup" aria-label="Payment method">
                {methods.map(m => (
                    <button
                        type="button"
                        key={m.id}
                        role="radio"
                        aria-checked={method === m.id}
                        className={`method-option${method === m.id ? ' selected' : ''}`}
                        onClick={() => setMethod(m.id)}
                    >
                        <span className="method-name">{m.name}</span>
                        <span className="method-note">{m.note}</span>
                    </button>
                ))}
            </div>

            {method === 'swish' && (
                <>
                    <div className="segmented">
                        <button type="button" className={`pill${swishMode === 'phone' ? ' active' : ''}`} onClick={() => setSwishMode('phone')}>
                            Phone number
                        </button>
                        <button type="button" className={`pill${swishMode === 'app' ? ' active' : ''}`} onClick={() => setSwishMode('app')}>
                            Open Swish on this device
                        </button>
                    </div>
                    {swishMode === 'phone' && (
                        <label>
                            Swish phone number
                            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} required placeholder="070 123 45 67" autoComplete="tel" />
                            <span className="hint">A payment request is sent to this number. Approve it in the Swish app.</span>
                        </label>
                    )}
                </>
            )}

            <button type="submit" className="btn-submit" disabled={busy}>
                {busy ? 'Starting payment…' : `Pay with ${methods.find(m => m.id === method)!.name}`}
            </button>
        </form>
    );
}
